/**
 * Vanilla animal loop: feeding, love mode, breeding, babies.
 *
 *  - Breeding food: cow / sheep -> wheat, chicken -> wheat seeds (vanilla
 *    also beetroot / melon / pumpkin / torchflower seeds), pig -> carrot /
 *    potato / beetroot, panda -> bamboo, fox -> glow berries
 *    (vanilla also sweet berries, which this game has no item for).
 *  - Feeding an adult that can fall in love: 30 s love mode (hearts every 10
 *    ticks). Two adults of one type in love within 8 blocks walk together and,
 *    after 3 s (60 ticks) within 3 blocks, make a baby; both get a 5 min
 *    cooldown and the player gets 1-7 XP.
 *  - Feeding a baby speeds its growth up by 10% of what is left.
 *  - Babies follow the nearest adult of their kind within 8 blocks.
 */

import { BAMBOO_ID, DANDELION_ID, SEAGRASS_ID } from '../blocks';
import { I } from '../game/itemIds';
import type { MobType } from '../game/types';
import { Animal, BREED_COOLDOWN, LOVE_TICKS } from './animals';
import type { Mob } from './mob';

export const BREED_FOOD: Partial<Record<MobType, readonly number[]>> = {
  cow: [I.WHEAT],
  sheep: [I.WHEAT],
  pig: [I.CARROT, I.POTATO, I.BEETROOT],
  chicken: [I.WHEAT_SEEDS, I.BEETROOT_SEEDS],
  horse: [I.GOLDEN_APPLE, I.GOLDEN_CARROT],
  donkey: [I.GOLDEN_APPLE, I.GOLDEN_CARROT],
  panda: [BAMBOO_ID],
  fox: [I.GLOW_BERRIES],
  rabbit: [I.CARROT, I.GOLDEN_CARROT, DANDELION_ID], goat: [I.WHEAT], turtle: [SEAGRASS_ID], mooshroom: [I.WHEAT],
  ocelot: [I.COD, I.SALMON], axolotl: [I.TROPICAL_FISH],
};

const MATE_RANGE = 8;
const BREED_DIST = 3;
const BREED_DELAY = 60;

export const isBreedFood = (type: MobType, item: number): boolean => BREED_FOOD[type]?.includes(item) ?? false;

/**
 * Player right-clicks `m` holding `item`. Returns true when the item was used
 * (caller consumes one outside creative).
 */
export function feedAnimal(m: Mob, item: number): boolean {
  if (!(m instanceof Animal) || !m.alive || !isBreedFood(m.type, item)) return false;
  if (m.baby) {
    m.growTicks = Math.max(1, m.growTicks - Math.floor(m.growTicks * 0.1));
    return true;
  }
  if (!m.canFallInLove) return false;
  m.loveTicks = LOVE_TICKS;
  return true;
}

export interface BreedingHooks {
  spawn(type: MobType, x: number, y: number, z: number): Mob | null;
  dropXp(amount: number, x: number, y: number, z: number): void;
  rnd(): number;
}

/** Make `type` baby at (x, y, z) (breeding, or a spawn egg used on a mob). */
export function spawnBaby(h: Pick<BreedingHooks, 'spawn'>, type: MobType, x: number, y: number, z: number): Animal | null {
  const m = h.spawn(type, x, y, z);
  if (!(m instanceof Animal)) { if (m) m.removed = true; return null; }
  m.setBaby(true);
  return m;
}

/** One 20 Hz pass over the mobs (EntityManager.tick). */
export function tickBreeding(mobs: readonly Mob[], h: BreedingHooks, tick: number): void {
  for (const a of mobs) {
    if (!(a instanceof Animal) || !a.alive) continue;
    if (a.loveTicks > 0 && !a.baby) {
      const b = a.mate;
      if (b && (!b.alive || b.loveTicks <= 0)) { a.mate = null; a.mateTicks = 0; }
      if (!a.mate) {
        let best: Animal | null = null, bd = MATE_RANGE * MATE_RANGE;
        for (const o of mobs) {
          if (o === a || !(o instanceof Animal) || !o.alive || o.type !== a.type || o.baby || o.loveTicks <= 0) continue;
          if (o.mate && o.mate !== a) continue;
          const d = (o.x - a.x) ** 2 + (o.y - a.y) ** 2 + (o.z - a.z) ** 2;
          if (d < bd) { bd = d; best = o; }
        }
        if (best) { a.mate = best; best.mate = a; a.mateTicks = best.mateTicks = 0; }
      }
      const m = a.mate;
      if (m && a.id < m.id) {
        const d = (m.x - a.x) ** 2 + (m.y - a.y) ** 2 + (m.z - a.z) ** 2;
        if (d < BREED_DIST * BREED_DIST) a.mateTicks++;
        if (a.mateTicks >= BREED_DELAY) breed(a, m, h);
      }
    } else if (a.baby && (tick + a.id) % 20 === 0) {
      // FollowParentGoal: nearest adult of its kind within 8 blocks
      let best: Animal | null = null, bd = MATE_RANGE * MATE_RANGE;
      for (const o of mobs) {
        if (!(o instanceof Animal) || o.baby || !o.alive || o.type !== a.type) continue;
        const d = (o.x - a.x) ** 2 + (o.y - a.y) ** 2 + (o.z - a.z) ** 2;
        if (d < bd) { bd = d; best = o; }
      }
      a.follow = best;
    }
  }
}

function breed(a: Animal, b: Animal, h: BreedingHooks): void {
  const baby = spawnBaby(h, a.type, a.x, a.y, a.z);
  for (const p of [a, b]) { p.loveTicks = 0; p.mate = null; p.mateTicks = 0; p.breedCooldown = BREED_COOLDOWN; }
  if (!baby) return;
  baby.follow = a;
  baby.inherit(a, b, h.rnd);
  // sheep lambs take a parent's colour (vanilla mixes dyes; picking one is the common case)
  if (a.type === 'sheep') baby.color = h.rnd() < 0.5 ? a.color : b.color;
  h.dropXp(1 + Math.floor(h.rnd() * 7), a.x, a.y + 0.5, a.z);
}
